import { ApiKey } from '@esri/arcgis-rest-auth';
import { queryDemographicData } from '@esri/arcgis-rest-demographics';

export const apiKey = 'AAPK257f7a9d4d62498bb75ae97979172ffbM7H8sYPqGiKQW9zPgwuFrdn9KDzhg0fLsCj7r7xkmX_6qyI8RURDB48gdNc4F23E';

const dateType = {
    single: 0,
    range: 1,
};

const dataVariables = [
    {
        sortOrder: 0,
        category: 'KeyUSFacts',
        name: 'TOTPOP_FY',
        decimals: 0,
        format: '{yyyy1} Projection',
        type: dateType.single,
        isBold: false,
    }, //2026 Projection (Esri 2021/2026)
    {
        sortOrder: 1,
        category: 'KeyUSFacts',
        name: 'TOTPOP_CY',
        decimals: 0,
        format: '{yyyy1} Estimate',
        type: dateType.single,
        isBold: true,
        isPercent: false,
    }, //2021 Estimate	(Esri 2021/2026)
    {
        sortOrder: 2,
        category: 'KeyUSFacts',
        name: 'TOTPOP10',
        decimals: 0,
        format: '{yyyy1} Census',
        type: dateType.single,
        isBold: false,
        isPercent: false,
    }, //2010 US Census
    {
        sortOrder: 3,
        category: 'KeyUSFacts',
        name: 'TOTPOP00',
        decimals: 0,
        format: '{yyyy1} Census',
        type: dateType.single,
        isBold: false,
        isPercent: false,
    }, //2000 US Census
    {
        sortOrder: 4,
        category: 'KeyUSFacts',
        name: 'POPGRWCYFY',
        decimals: 2,
        format: "Growth '{yy1}-'{yy2}",
        type: dateType.range,
        isBold: true,
        isPercent: true,
    }, //Growth '21-'26	(Esri 2021/2026)
    {
        sortOrder: 5,
        category: 'PopulationTotals',
        name: 'POPGRW20CY',
        decimals: 2,
        format: "Growth '{yy1}-'{yy2}",
        type: dateType.range,
        isBold: false,
        isPercent: true,
    }, //Growth '10-'21	(Esri 2021/2026)
    {
        sortOrder: 7,
        category: 'HouseholdIncome',
        name: 'AVGHINC_CY',
        decimals: 0,
        format: 'Avg HH Inc. {yyyy1}',
        type: dateType.range,
        isBold: true,
    },
];

export const getDemographicsTable = async (location) => {
    const data = await getDemographicData(location);

    let table =
        "<table><thead><tr><td style='font-weight:bold'>Population</td><td>1 Mile</td><td>3 Mile</td><td>5 Mile</td></tr></thead>";
    data.forEach((elm, index) => {
        let style = elm.isBold ? 'font-weight:bold;' : '';
        style += elm.isPercent ? 'font-style:italic;' : '';
        style = style ? `style='${style}'` : '';
        table += `<tr><td ${style}>${elm.alias}</td><td ${style}>${elm['1 Mile']}</td><td ${style}>${elm['3 Mile']}</td><td ${style}>${elm['5 Mile']}</td></tr>`;
    });
    table += '</table>';
    return `<div className='demographics'>${table}</div>`;
};

export const getDemographicData = async (location) => {
    const authentication = new ApiKey({
        key: apiKey,
    });

    const miles = [1, 3, 5];
    const studyAreasOptions = '{"areaType":"RingBuffer", "bufferUnits":"Miles", "bufferRadii":[' + miles.join() + ']}';

    const varNames = dataVariables.map((e) => {
        return `${e.category}.${e.name}`;
    });

    const response = await queryDemographicData({
        studyAreas: [{ geometry: { x: location.longitude, y: location.latitude } }],
        params: { studyAreasOptions: studyAreasOptions },
        analysisVariables: varNames,
        authentication,
    });

    if (
        !response ||
        !response.results ||
        response.results.length !== 1 ||
        !response.results[0].value?.FeatureSet ||
        response.results[0].value.FeatureSet.length !== 1
    ) {
        return null;
    }

    //will return data as per https://developers.arcgis.com/arcgis-rest-js/demographic-data/query-demographic-data/
    //See https://esri.github.io/arcgis-rest-js/api/demographics/IGeoenrichmentResult/ for further details of returned structures
    const data = response.results[0].value.FeatureSet[0];
    const output = [];
    for (let i = 0; i < dataVariables.length; i++) {
        const variable = dataVariables[i].name;
        if (data.fieldAliases) {
            const formattedName = formatName(dataVariables[i], data.fieldAliases[variable]);
            const row = {
                name: variable,
                alias: formattedName,
                isBold: dataVariables[i].isBold,
                isPercent: dataVariables[i].isPercent,
                sortOrder: dataVariables[i].sortOrder,
            };
            output.push(row);
            for (let x = 0; x < miles.length; x++) {
                const source = data.features[x].attributes;
                const fieldName = miles[x] + ' Mile';
                row[fieldName] = source[variable];
            }
        }
    }

    calculate20002010GrowthRate(output, miles);

    //Format the output
    for (let i = 0; i < dataVariables.length; i++) {
        const row = output.find((e) => {
            return e.name === dataVariables[i].name;
        });
        for (let x = 0; x < miles.length; x++) {
            const fieldName = miles[x] + ' Mile';
            row[fieldName] =
                row[fieldName].toLocaleString('en-US', {
                    minimumFractionDigits: dataVariables[i].decimals,
                    maximumFractionDigits: dataVariables[i].decimals,
                }) + (dataVariables[i].isPercent ? '%' : '');
        }
    }

    //Sort by Order
    output.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));

    return output;
};

const formatName = (variable, alias) => {
    let formattedText = '';

    const yrs = alias.match(/\d+/g);
    if (variable.type === dateType.range) {
        //Year range
        formattedText = formatDateString(variable.format, yrs[0], yrs[1]);
    } else {
        //Single Year
        formattedText = formatDateString(variable.format, yrs[0]);
    }
    return formattedText;
};

const formatDateString = (format, y1, y2) => {
    const y1S = y1.toString();
    const y2S = y2?.toString() ?? '';
    let retVal = format.replace('{yyyy1}', y1S);
    retVal = retVal.replace('{yyyy2}', y2S);
    retVal = retVal.replace('{yy1}', y1S.substring(2));
    retVal = retVal.replace('{yy2}', y2S.substring(2));
    return retVal;
};

const calculate20002010GrowthRate = (data, miles) => {
    const year2010Pop = data.find((row) => {
        return row.name === 'TOTPOP10';
    });
    const year2000Pop = data.find((row) => {
        return row.name === 'TOTPOP00';
    });
    const row = {
        name: 'Growth 00-10',
        alias: "Growth '00-'10",
        sortOrder: 6,
        isBold: false,
        isPercent: true,
    };
    for (let x = 0; x < miles.length; x++) {
        const fieldName = miles[x] + ' Mile';
        row[fieldName] =
            (((year2010Pop[fieldName] - year2000Pop[fieldName]) / year2000Pop[fieldName] / 10) * 100).toFixed(2) + '%';
    }
    data.push(row);
};
